import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Signal,
  computed,
  effect,
  signal,
  untracked
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { IViewPair } from "src/app/interfaces/interfaces";
import { ExtenderService } from "src/app/services/extender.service";
import { FormService } from "src/app/services/form.service";
import { IconService } from "src/app/services/icon.service";
import { PagingService } from "src/app/services/paging.service";
import {
  VehicleWarningLightsResponse,
  WorkshopSystemEnum,
  PackageResponse,
  ExternalTyreUrlResponse,
  InsuranceCompanyResponse,
  DealerResponse,
  PackageTagEnum,
  VehicleSearchResponse
} from "src/domain/client";
import { DialogTypeEnum } from "src/app/classes/enums";

import { DialogComponent } from "../../layout/dialog/dialog.component";
import { PageBaseComponent } from "../../base/page-base/page-base.component";
import { PackagesFilterPipe } from "src/app/pipes/packages-filter.pipe";
import { PackagesServiceFilterPipe } from "src/app/pipes/packages-service-filter.pipe";
import { CarBrandPipe } from "src/app/pipes/car-brand.pipe";
import { BusinessProposalService } from "src/app/services/business-proposal.service";
import {
  createChooseSpecificationsForm,
  updateWorkshopSelectionForm
} from "./choose-specification-form.helper";
import { PageContainerComponent } from "../../layout/page-container/page-container.component";
import { LoadingComponent } from "../../layout/loading/loading.component";
import { RequestFailedComponent } from "../../layout/request-failed/request-failed.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SpecsTechUpdateSectionComponent } from "./specs-tech-update-section.component";
import { SpecsServiceSectionComponent } from "./specs-service-section.component";
import { SpecsTroubleshootingSectionComponent } from "./specs-troubleshooting-section.component";
import { SpecsWheelSectionComponent } from "./specs-wheel-section.component";
import { SpecsWindscreenSectionComponent } from "./specs-windscreen-section.component";
import { SpecsPpsSectionComponent } from "./specs-pps-section.component";
import { SpecsOtherSectionComponent } from "./specs-other-section.component";
import { SpecsAdditionalServicesSectionComponent } from "./specs-additional-services-section.component";
import { SpecsRightColumnComponent } from "./specs-right-column.component";
import { Store } from "@ngrx/store";
import {
  AppError,
  loadSpecificationsPageData,
  selectBasePackages,
  selectCarInfo,
  selectError,
  selectExternalTyreUrl,
  selectInsuranceProviders,
  selectIsLoading,
  selectIsPartialLoading,
  selectSelectedDealer,
  selectSelectedPackages,
  selectSelectedWorkshopSystems,
  selectServiceAdditionalPackagesStep4,
  selectWarningLights,
  setRejectedIsOnlyTyre,
  submitWorkshopSystems,
  updateProviderIds,
  updateSelectedPackages
} from "src/app/state";

import { debounceTime, switchMap, take } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { TrackingService } from "src/app/services/tracking.service";
@Component({
  selector: "app-choose-specifications",
  templateUrl: "./choose-specifications.component.html",
  styleUrl: "./choose-specifications.component.scss",
  imports: [
    PageContainerComponent,
    LoadingComponent,
    RequestFailedComponent,
    TranslateModule,
    SpecsTechUpdateSectionComponent,
    SpecsServiceSectionComponent,
    SpecsTroubleshootingSectionComponent,
    SpecsWheelSectionComponent,
    SpecsWindscreenSectionComponent,
    SpecsPpsSectionComponent,
    SpecsOtherSectionComponent,
    SpecsAdditionalServicesSectionComponent,
    SpecsRightColumnComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChooseSpecificationsComponent
  extends PageBaseComponent
  implements OnInit, OnDestroy
{
  public isLoadingFile = false;

  public loadingAfterServiceChange = false;
  public additionalServicesAdded = false;

  public additionalServices: PackageResponse[] = [];

  public WorkshopSystemEnum = WorkshopSystemEnum;

  readonly specificationsForm = signal<FormGroup | null>(null);
  readonly selectedDealer: Signal<DealerResponse | null>;
  readonly basePackages: Signal<PackageResponse[] | null>;
  readonly additionalPackages?: Signal<PackageResponse[] | null>;
  readonly externalTyreUrlResponse: Signal<ExternalTyreUrlResponse | null>;
  readonly selectedPackages: Signal<any | null>;
  readonly isLoading: Signal<boolean>;
  readonly isPartialLoading: Signal<boolean>;
  readonly error: Signal<AppError | null>;
  readonly warningLights: Signal<VehicleWarningLightsResponse[] | null>;
  readonly insuranceProviders: Signal<InsuranceCompanyResponse[] | null>;
  readonly selectedWorkshopSystems: Signal<WorkshopSystemEnum[] | null>;
  readonly rejectedIsOnlyTyre? = signal<boolean>(false);

  readonly carInfo?: Signal<VehicleSearchResponse | null>;

  readonly insuranceViewPairs = computed<IViewPair[]>(() => {
    return this.insuranceProviders().map((i: InsuranceCompanyResponse) => ({
      value: i.insuranceCompanyId.toString(),
      view: i.name
    }));
  });

  readonly otherPlaceholder = computed(() => {
    if (
      this.selectedWorkshopSystems()?.length === 1 &&
      this.selectedWorkshopSystems()[0] === WorkshopSystemEnum.Other
    ) {
      return this.translateService.instant(
        "choose-specifications.other.placeholder-min-5"
      );
    }
    return this.translateService.instant(
      "choose-specifications.other.placeholder"
    );
  });

  constructor(
    public formService: FormService,
    public iconService: IconService,
    public extenderService: ExtenderService,
    private pagingService: PagingService,
    public dialog: MatDialog,
    private carBrandPipe: CarBrandPipe,
    private packageFilterPipe: PackagesFilterPipe,
    private packageServiceFilterPipe: PackagesServiceFilterPipe,
    public businessProposalService: BusinessProposalService,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private store: Store
  ) {
    super();
    this.formService.chooseSpecificationsForm.markAsUntouched();
    this.formService.chooseSpecificationsForm.markAsPristine();
    this.formService.previousFormValue =
      this.formService.chooseSpecificationsForm.value;

    this.selectedDealer = this.store.selectSignal(selectSelectedDealer);
    this.externalTyreUrlResponse = this.store.selectSignal(
      selectExternalTyreUrl
    );

    this.isLoading = this.store.selectSignal(selectIsLoading);
    this.isPartialLoading = this.store.selectSignal(selectIsPartialLoading);
    this.error = this.store.selectSignal(selectError);

    this.basePackages = this.store.selectSignal(selectBasePackages);
    this.additionalPackages = this.store.selectSignal(
      selectServiceAdditionalPackagesStep4
    );
    this.warningLights = this.store.selectSignal(selectWarningLights);
    this.selectedWorkshopSystems = this.store.selectSignal(
      selectSelectedWorkshopSystems
    );

    this.insuranceProviders = this.store.selectSignal(selectInsuranceProviders);

    this.selectedPackages = this.store.selectSignal(selectSelectedPackages);
    this.carInfo = this.store.selectSignal(selectCarInfo);

    const externalTyreUrl = this.store.selectSignal(selectExternalTyreUrl);

    const shouldCreateNewForm =
      this.formService.chooseSpecificationsForm.controls.invalidateByDefault ||
      this.businessProposalService.isBusinessProposalActive;

    effect(() => {
      const basePackages = this.basePackages();
      const additionalPackages = this.additionalPackages();
      const warnLights = this.warningLights();
      const selectedWorkshopSystems = this.selectedWorkshopSystems();
      const selectedPackages = untracked(this.selectedPackages);

      if (
        !this.isLoading() &&
        !this.error() &&
        basePackages &&
        additionalPackages &&
        selectedWorkshopSystems &&
        shouldCreateNewForm
      ) {
        const newForm = createChooseSpecificationsForm(
          basePackages,
          additionalPackages,
          selectedWorkshopSystems,
          warnLights,
          this.businessProposalService,
          this.packageServiceFilterPipe,
          this.packageFilterPipe,
          selectedPackages
        );
        this.formService.updateForm(newForm);
        this.formService.chooseSpecificationsForm.markAsTouched();
      }
    });

    const formChanges = toSignal(
      this.formService.chooseSpecificationsForm$.pipe(
        switchMap((form) => form.valueChanges.pipe(debounceTime(300)))
      )
    );

    effect(() => {
      const changes = formChanges();
      if (changes) {
        const rawValue =
          this.formService.chooseSpecificationsForm.getRawValue();
        this.store.dispatch(
          updateSelectedPackages({ selectedPackages: rawValue })
        );
      }
    });

    effect(
      () => {
        const urlResponse = externalTyreUrl();
        const systems = this.selectedWorkshopSystems();
        const info = this.carInfo();

        if (
          urlResponse &&
          systems &&
          info &&
          !!urlResponse.externalTyreUrl &&
          systems.length === 1 &&
          systems.includes(WorkshopSystemEnum.Wheel)
        ) {
          this.rejectedIsOnlyTyre.set(true);
          this.store.dispatch(setRejectedIsOnlyTyre({ rejected: true }));
          document.getElementById("vbo-header")?.scrollIntoView(true);
          const ref = this.dialog.open(DialogComponent, {
            data: {
              componentName: DialogTypeEnum.externalTyre,
              title: "Däckbokningen befinner sig på en extern sida", // Should be translated
              brand: this.carBrandPipe.transform(info)
            },
            disableClose: true
          });
          ref
            .afterClosed()
            .pipe(take(1))
            .subscribe((result: boolean) => {
              if (result) {
                let url = urlResponse.externalTyreUrl;
                if (!url.startsWith("http://") && !url.startsWith("https://")) {
                  url = "https://" + url;
                }
                try {
                  const parsed = new URL(url);
                  if (
                    parsed.protocol === "http:" ||
                    parsed.protocol === "https:"
                  ) {
                    window.location.href = parsed.href;
                  }
                } catch {
                  console.error("Invalid external tyre URL:", url);
                }
              }
            });
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const systems = this.selectedWorkshopSystems();
        const packages = this.basePackages();
        const saved = untracked(this.selectedPackages);

        if (
          systems &&
          packages &&
          !saved?.serviceForm?.selectedContainer &&
          systems.includes(WorkshopSystemEnum.Service)
        ) {
          const servicePackages =
            this.packageServiceFilterPipe.transform(packages);
          const recommended = servicePackages.find((p) =>
            p.tags.includes(PackageTagEnum.Recommended)
          );

          if (recommended) {
            const subSelections: Record<string, boolean> = {};
            recommended.subPackages.forEach((sp) => {
              if (
                sp.tags.includes(PackageTagEnum.Recommended) ||
                sp.tags.includes(PackageTagEnum.MainPackage)
              ) {
                subSelections[sp.id] = true;
              }
            });

            const defaultSelection = {
              ...saved,
              serviceForm: {
                selectedContainer: recommended.id,
                [recommended.id]: subSelections
              }
            };
            this.store.dispatch(
              updateSelectedPackages({ selectedPackages: defaultSelection })
            );
          }
        }
      },
      { allowSignalWrites: true }
    );

    if (this.businessProposalService.isBusinessProposalActive) {
      updateWorkshopSelectionForm(
        this.formService.workshopSystemSelectionForm,
        this.selectedWorkshopSystems()
      );

      const workshopSystemChanges = toSignal(
        this.formService.workshopSystemSelectionForm.valueChanges
      );

      effect(() => {
        const changes = workshopSystemChanges();
        if (changes) {
          const newSelection = Object.keys(changes).filter(
            (key) => changes[key] === true
          ) as WorkshopSystemEnum[];

          this.store.dispatch(
            submitWorkshopSystems({ selectedSystems: newSelection })
          );
        }
      });
    }
  }

  ngOnInit(): void {
    if (!this.basePackages()) {
      this.store.dispatch(
        loadSpecificationsPageData({ isPartialReload: false })
      );
      this.trackingService.hasTrackedServiceTypesOnce = false;
    }
  }

  get troubleshootingForm(): FormGroup<any> {
    return this.formService.chooseSpecificationsForm.controls
      .troubleshootingForm as FormGroup<any>;
  }
  get wheelForm(): FormGroup<any> {
    return this.formService.chooseSpecificationsForm.controls.wheelForm;
  }
  get windscreenForm(): FormGroup<any> {
    return this.formService.chooseSpecificationsForm.controls
      .windscreenForm as FormGroup<any>;
  }

  get additionalPackagesForm(): FormGroup<any> {
    return this.formService.chooseSpecificationsForm.controls
      .additionalPackagesForm;
  }

  get packages(): PackageResponse[] {
    return this.basePackages();
  }

  get proposalPackages(): PackageResponse[] {
    return this.businessProposalService.businessProposal$?.value.packages || [];
  }

  resetWithValidServiceAgreementDealers(): void {
    const validDealers =
      this.carInfo()?.serviceAgreementResponse?.data?.validDealers;
    this.store.dispatch(updateProviderIds({ providerIds: validDealers }));
    this.formService.resetWithValidServiceAgreementDealers(validDealers);
    this.pagingService.navigateToStep(3);
  }

  ngOnDestroy(): void {
    this.cleanUp();
  }
}
